import { Utils as QbUtils } from "@react-awesome-query-builder/mui"

import { getConfig } from "shared/ui/berry-jass-extended/QueryBuilder"

const EMPTY_TREE = { id: "", type: "group" }

export const VALUELESS_OPERATORS = ["is_empty", "is_not_empty"]

/**
 * Compiles a condition AST into the `{ tree, sql }` pair a form needs: `tree` for the
 * widget, `sql` for persistence. Throws rather than returning a verdict — an AST that
 * will not compile is unusable, and the caller has nothing to fall back to.
 *
 * @param {object | null} conditionRoot - AST root, or null for "no condition"
 * @param {object} fields - the SAME merged fields config the widget is given
 * @param {object} [customOperators] - the SAME operator overrides the widget is given
 * @returns {{ tree: object, sql: string }}
 */
export const compileCondition = (conditionRoot, fields, customOperators) => {
  if (!conditionRoot) {
    return { tree: EMPTY_TREE, sql: "" }
  }

  // The query-builder library's tree ALWAYS
  // requires a "group" node at the root
  const rootNode =
    conditionRoot.type !== "group"
      ? { type: "group", op: "AND", children: [conditionRoot] }
      : conditionRoot

  const conditionNode = compileConditionNode(rootNode, fields)

  // No locale argument: `getConfig` ignores it and rebuilds its labels from the
  // current I18n locale on every call. The widget passes one only as a `useMemo` key.
  const config = { ...getConfig(undefined, customOperators), fields }

  // sanitizeTree, isValidTree and sqlFormat need Immutable, not plain JSON.
  const tree = QbUtils.loadTree(conditionNode)
  // Applies the field config: fills in metadata, drops what it rejects.
  //
  // `removeIncompleteRules` is spelled out because the deprecated `checkTree` this
  // replaces read the three removal flags off `config.settings.*OnLoad`, and the library
  // defaults that one to false where `sanitizeTree` defaults it to true. Passing it is
  // what keeps the two identical: left out, an incomplete or unknown-operator rule is
  // dropped here instead of surviving into the leaf count below.
  const { fixedTree } = QbUtils.sanitizeTree(tree, config, {
    removeIncompleteRules: config.settings.removeIncompleteRulesOnLoad
  })

  if (!QbUtils.isValidTree(fixedTree, config)) {
    throw new Error("Invalid condition tree")
  }

  // `sanitizeTree` sanitizes rather than rejects: an unknown field is silently dropped at
  // any depth, and `isValidTree` then reports true on what is left. A missing leaf means
  // part of the condition was thrown away.
  if (
    countRuleLeaves(QbUtils.getTree(fixedTree)) !==
    countRuleLeaves(conditionNode)
  ) {
    throw new Error("Condition rules rejected by the field config")
  }

  // returns undefined for a tree it cannot render
  const sql = QbUtils.sqlFormat(fixedTree, config)

  if (sql === undefined) {
    throw new Error("Condition produced no SQL")
  }

  return { tree: QbUtils.getTree(fixedTree), sql }
}

const countRuleLeaves = (node) => {
  if (node?.type === "rule") {
    return 1
  }

  if (node?.type === "group" && Array.isArray(node.children1)) {
    return node.children1.reduce(
      (sum, child) => sum + countRuleLeaves(child),
      0
    )
  }

  return 0
}

const compileConditionNode = (node, fields) => {
  switch (node.type) {
    case "group":
      return {
        id: QbUtils.uuid(),
        type: "group",
        properties: {
          conjunction: node.op,
          not: node.not === true
        },
        children1: node.children.map((child) =>
          compileConditionNode(child, fields)
        )
      }
    case "rule":
      return {
        id: QbUtils.uuid(),
        type: "rule",
        properties: {
          field: node.field,
          operator: node.operator,
          value: compileValue(node),
          valueSrc: ["value"],
          valueType: compileValueType(node, fields)
        }
      }
    default:
      throw new Error(`Unknown condition node type: ${node.type}`)
  }
}

const compileValue = (node) =>
  VALUELESS_OPERATORS.includes(node.operator) ? [] : [node.value]

const compileValueType = (node, fields) => [
  fields[node.field]?.type === "select" ? "select" : "text"
]

/**
 * The inverse of `compileCondition`: serializes the query-builder's own plain tree shape
 * (as produced by `QbUtils.getTree()`) back into a condition AST.
 *
 * A group with no children (including the initial empty-tree sentinel
 * `{ id: "", type: "group" }`) collapses to `null`, which is what the AST uses for "no
 * condition". An incomplete rule (no field/operator selected yet, mid-edit) is dropped
 * rather than returned as partial garbage, collapsing its parent group the same way.
 * Unlike the forward direction this never throws — reading a live widget tree, a
 * half-finished rule is an ordinary state, not a contract violation.
 *
 * @param {object} tree - a plain query-builder tree, or null/undefined
 * @returns {object | null}
 */
export const parseConditionTree = (tree) => {
  if (!tree || tree.type !== "group") {
    return null
  }

  return parseGroupNode(tree)
}

const parseGroupNode = (node) => {
  const children = (node.children1 || []).map(parseChildNode).filter(Boolean)

  if (children.length === 0) {
    return null
  }

  return {
    type: "group",
    op: node.properties?.conjunction || "AND",
    not: node.properties?.not === true,
    children
  }
}

const parseChildNode = (node) => {
  if (!node) {
    return null
  }

  return node.type === "group" ? parseGroupNode(node) : parseRuleNode(node)
}

const parseRuleNode = (node) => {
  if (node.type !== "rule") {
    return null
  }

  const { field, operator, value } = node.properties || {}

  if (!field || !operator) {
    return null
  }

  return {
    type: "rule",
    field,
    operator,
    value: VALUELESS_OPERATORS.includes(operator) ? null : value?.[0] ?? null
  }
}
